import { useCallback, useMemo } from 'react';

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    // Si la fecha viene en formato YYYYMMDD
    if (dateString.length === 8 && !isNaN(Number(dateString))) {
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${day}-${month}-${year}`;
    }
    
    // Si la fecha viene en otro formato, intentar parsearla normalmente
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    return '';
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return '';
  }
};

const formatDecimalAmount = (amount: number): string => {
  try {
    if (typeof amount !== 'number') {
      console.warn('formatDecimalAmount received non-number:', amount);
      return '0';
    }
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      style: 'decimal'
    }).format(amount);
  } catch (error) {
    console.error('Error formatting amount:', error);
    return '0';
  }
};

export function useOutstandingChecker(custom:any = null) {
  const operatorMap = useMemo<Record<string, string>>(() => ({
    "215": "MOVISTAR",
    "219": "CLARO",
    "220": "ENTEL",
    "235": "VTR MOVIL S.A.",
    "236": "VIRGIN",
    "250": "MUNDO MOVIL",
  }), []);
  
  const checkHasOutstanding = useCallback((customData = custom) => {
    if (
      !customData ||
      !customData.outstandingsinfo ||
      !Array.isArray(customData.outstandingsinfo.outstandings)
    ) {
      return false;
    }

    return customData.outstandingsinfo.outstandings.some((outstanding:any) =>
      Array.isArray(outstanding.outstandingAmounts) &&
      outstanding.outstandingAmounts.some((item:any) => item.outstandingAmount > 0)
    );
  }, [custom]);

  const getOutstandingIndexes = useCallback((customData = custom) => {
    if (
      !customData ||
      !customData.outstandingsinfo ||
      !Array.isArray(customData.outstandingsinfo.outstandings)
    ) {
      return [];
    }

    return customData.outstandingsinfo.outstandings.reduce((indexes:any, outstanding:any, index:any) => {
      const hasPositiveAmount =
        Array.isArray(outstanding.outstandingAmounts) &&
        outstanding.outstandingAmounts.some((item:any) => item.outstandingAmount > 0);

      if (hasPositiveAmount) {
        indexes.push(index);
      }
      return indexes;
    }, []);
  }, [custom]);

  const getPositiveOutstandingItems = useCallback((customData = custom) => {
      if (!customData?.outstandingsinfo?.outstandings) return [];

      return customData.outstandingsinfo.outstandings.flatMap((outstanding:any) =>
        Array.isArray(outstanding.outstandingAmounts)
          ? outstanding.outstandingAmounts.filter((item:any) => item.outstandingAmount > 0)
          : []
      );
    },
    [custom]
  );

  const getOutstandingDetails = useCallback((customData = custom) => {
      if (
        !customData ||
        !customData.outstandingsinfo ||
        !Array.isArray(customData.outstandingsinfo.outstandings)
      ) {
        return [];
      }

      const { outstandings, relatedParty = [] } = customData.outstandingsinfo || {};

      return outstandings!.flatMap((outstanding:any, index:any) => {
        const mnoId = relatedParty[index]?.id;
        const mno = operatorMap[mnoId || ""] || "OTROS";

        return (outstanding.outstandingAmounts || [])
          .filter((item:any) => item.outstandingAmount > 0)
          .map((item:any) => {
            const amount = parseFloat(item.outstandingAmount);
            const formattedDate = formatDate(item.outstandingBillingDate);
            
            return {
              outstandingAmount: amount,
              formattedAmount: formatDecimalAmount(amount),
              outstandingBillingDate: formattedDate,
              mno
            };
          });
      });
    },
    [custom, operatorMap]
  );

  return {
    checkHasOutstanding,
    getOutstandingIndexes,
    getPositiveOutstandingItems,
    getOutstandingDetails,
  };
}